import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Divider, Chip, Paper,
  TextField, Button, Snackbar, Stack, Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { publicPassAPI } from '../services/api';
import HealthPassCard from '../components/TravelerScreening/HealthPassCard';
import type { PassRecord } from '../components/TravelerScreening/HealthPassCard';
import { formatPortName } from '../utils/formatPortName';

const isPassDebugEnabled = () => {
  try {
    const qp = new URLSearchParams(window.location.search);
    const byQuery = qp.get('passDebug') === '1';
    const byLs = window.localStorage.getItem('passDebug') === '1';
    return byQuery || byLs;
  } catch {
    return false;
  }
};

const passDebug = (...args: any[]) => {
  if (!isPassDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log('[PassDebug]', ...args);
};

const fmt = (val: any) => {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
};

const fmtDate = (val: any) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-GB', {
      timeZone: 'Africa/Nairobi',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }
  catch { return String(val); }
};

const EBOLA_SYMPTOM_LABELS: Record<string, string> = {
  headache: 'Headache',
  muscle_joint_pain: 'Muscle / Joint Pain',
  vomiting_nausea: 'Vomiting / Nausea',
  diarrhoea: 'Diarrhoea',
  abdominal_pain: 'Abdominal Pain',
  bleeding_bruising: 'Unusual Bleeding / Bruising',
  haemorrhagic_rash: 'Haemorrhagic Rash',
  conjunctival: 'Conjunctival Redness',
  sore_throat: 'Sore Throat',
  difficulty_swallowing: 'Difficulty Swallowing',
  altered_consciousness: 'Altered Consciousness',
};

const EBOLA_EXPOSURE_LABELS: Record<string, string> = {
  healthcare_lab_work: 'Healthcare / Lab Work',
  funeral_body_prep: 'Funeral / Body Preparation',
  contact_animals: 'Contact with Animals',
  bushmeat_handling: 'Bushmeat Handling',
  forest_cave_exposure: 'Forest / Cave Exposure',
};

const SectionTitle: React.FC<{ label: string }> = ({ label }) => (
  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 1, color: '#555', mt: 2.5, mb: 1 }}>
    {label}
  </Typography>
);

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8, gap: 1 }}>
    <Typography sx={{ width: '45%', flexShrink: 0, fontSize: '0.8rem', color: '#777', fontWeight: 500, wordBreak: 'break-word' }}>{label}</Typography>
    <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: '#111', wordBreak: 'break-word' }}>{value}</Typography>
  </Box>
);

/** Skeleton shown while the pass loads. After a few seconds on a slow
 *  connection it adds a reassuring hint so the wait never looks frozen. */
const PassLoading: React.FC = () => {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 4000);
    return () => clearTimeout(t);
  }, []);
  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', px: 2, py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Skeleton variant="text" width={80} height={32} sx={{ mx: 'auto' }} />
        <Skeleton variant="text" width={240} height={20} sx={{ mx: 'auto' }} />
      </Box>
      <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2, mb: 3 }} />
      <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 3 }}>
        <CircularProgress size={18} />
        <Typography sx={{ color: '#555', fontSize: '0.9rem' }}>
          {slow ? 'Still loading — your connection seems slow…' : 'Loading health pass…'}
        </Typography>
      </Box>
    </Box>
  );
};

const PublicPassPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Officer actions
  const [tempInput, setTempInput] = useState('');
  const [savingTemp, setSavingTemp] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadPass = () => {
    if (!token) { setError('No token in URL.'); setLoading(false); return; }
    passDebug('Calling public pass API', { token });

    publicPassAPI.getPass(token)
      .then((d: any) => {
        passDebug('API success', d);
        setData(d);
        setTempInput(d?.screening?.temperature != null ? String(d.screening.temperature) : '');
        setLoading(false);
      })
      .catch((e: any) => {
        const status = e?.response?.status;
        passDebug('API error', {
          message: e?.message,
          status,
          data: e?.response?.data,
        });
        if (status === 401 || status === 403) {
          navigate('/login', { state: { from: location.pathname }, replace: true });
          return;
        }
        const detail = e?.response?.data?.detail;
        if (status === 410 || (typeof detail === 'string' && /expir/i.test(detail))) {
          setError(typeof detail === 'string' ? detail : 'This QR code has expired. The health pass is no longer valid.');
        } else {
          setError(typeof detail === 'string' ? detail : 'Pass not found or link is invalid.');
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    passDebug('Mounted PublicPassPage');
    passDebug('Location', {
      href: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      origin: window.location.origin,
    });
    passDebug('Router params', { token });

    // If the scanner already fetched this pass (same staff-authenticated,
    // HMAC-verified endpoint) and handed it over via navigation state, render
    // it immediately instead of making a second round trip. Falls back to a
    // normal fetch on direct-URL opens / refreshes, where there is no state.
    const prefetched = (location.state as any)?.prefetched;
    if (prefetched) {
      passDebug('Using prefetched pass data from scanner (no refetch)');
      setData(prefetched);
      setTempInput(prefetched?.screening?.temperature != null ? String(prefetched.screening.temperature) : '');
      setLoading(false);
    } else {
      loadPass();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) return <PassLoading />;

  if (error || !data) return (
    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Alert severity="error" sx={{ fontSize: '1rem', fontWeight: 600 }}>
        {/expir/i.test(error || '') ? 'QR code is expired' : (error || 'Pass not found.')}
      </Alert>
      {/expir/i.test(error || '') && (
        <Typography sx={{ mt: 1.5, color: '#555', fontSize: '0.95rem' }}>
          {error}
        </Typography>
      )}
      {!/expir/i.test(error || '') && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#999' }}>Token: {token}</Typography>
      )}
    </Box>
  );

  const { pass_record, screening, screening_id } = data;
  const isEbola = screening?.source === 'whatsapp';
  const ebolaData = screening?.ebola_screening_data || {};

  const passRecord: PassRecord | null = pass_record ? {
    pass_type:         pass_record.pass_type,
    full_name:         pass_record.full_name,
    id_number:         pass_record.id_number,
    country_of_origin: screening?.country_of_origin,
    screening_type:    pass_record.screening_type,
    screening_status:  pass_record.screening_status,
    action_required:   pass_record.action_required,
    next_step:         pass_record.next_step,
    issued_at:         pass_record.issued_at,
    valid_until:       pass_record.valid_until,
    id:                pass_record.id,
    screening_id:      screening_id,
    pass_token:        token,
  } : null;

  const symptoms = isEbola
    ? ((ebolaData.symptoms || []) as string[])
        .filter((s) => s && s !== 'none')
        .map((s) => EBOLA_SYMPTOM_LABELS[s] || s)
    : [
        screening?.symptom_fever     && 'Fever',
        screening?.symptom_cough     && 'Cough',
        screening?.symptom_breathing && 'Difficulty Breathing',
        screening?.symptom_vomiting  && 'Vomiting',
        screening?.symptom_diarrhea  && 'Diarrhea',
        screening?.symptom_jaundice  && 'Jaundice',
        screening?.symptom_rash      && 'Rash',
        screening?.symptom_bleeding  && 'Bleeding',
      ].filter(Boolean) as string[];

  const exposures = Object.entries(EBOLA_EXPOSURE_LABELS)
    .filter(([key]) => ebolaData[key])
    .map(([, label]) => label);

  const handleSaveTemperature = async () => {
    const temp = parseFloat(tempInput);
    if (Number.isNaN(temp)) {
      setSnackbar('Enter a valid temperature.');
      return;
    }
    setSavingTemp(true);
    try {
      await publicPassAPI.updateTemperature(screening_id, temp);
      setSnackbar('Temperature updated.');
      loadPass();
    } catch {
      setSnackbar('Failed to update temperature.');
    } finally {
      setSavingTemp(false);
    }
  };

  const handleClearForTravel = async () => {
    setClearing(true);
    try {
      await publicPassAPI.clearForTravel(screening_id);
      setSnackbar('Pass updated — cleared for travel.');
      loadPass();
    } catch {
      setSnackbar('Failed to clear for travel.');
    } finally {
      setClearing(false);
    }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const res = await publicPassAPI.sendReportWhatsapp(screening_id);
      setSnackbar(res?.message || 'Request sent.');
    } catch {
      setSnackbar('Failed to send report.');
    } finally {
      setSendingReport(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', px: 2, py: 4 }}>
      {/* Back to screening list */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', color: '#555', mb: 2 }}
        onClick={() => navigate('/dashboard/traveler-screening')}
      >
        <ArrowBackIcon fontSize="small" />
        <Typography sx={{ fontSize: '0.85rem' }}>Back to Screening List</Typography>
      </Box>

      {/* Branding */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" color="#1f93ff">UHAI</Typography>
        <Typography variant="body2" color="text.secondary">Republic of Kenya · Ministry of Health</Typography>
      </Box>

      {/* Pass Card */}
      {passRecord ? (
        <HealthPassCard passRecord={passRecord} />
      ) : (
        <Alert severity="warning">No health pass generated for this screening.</Alert>
      )}

      {/* Officer Actions */}
      <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1.5 }}>Officer Actions</Typography>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <ThermostatIcon color="action" />
            <TextField
              size="small"
              label="Temperature (°C)"
              type="number"
              value={tempInput}
              onChange={(e) => setTempInput(e.target.value)}
              inputProps={{ step: 0.1, min: 30, max: 45 }}
              sx={{ width: 180 }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={savingTemp}
              onClick={handleSaveTemperature}
              startIcon={savingTemp ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Save Temperature
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={clearing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              disabled={clearing || screening?.cleared_for_travel || pass_record?.pass_type === 'cleared'}
              onClick={handleClearForTravel}
            >
              {screening?.cleared_for_travel || pass_record?.pass_type === 'cleared'
                ? 'Cleared for Travel'
                : 'Generate Clear for Travel'}
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={sendingReport ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon />}
              disabled={sendingReport}
              onClick={handleSendReport}
            >
              Send Report to WhatsApp
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Full Q&A */}
      {screening && (
        <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>Screening Details</Typography>
          <Divider sx={{ mb: 1 }} />

          {isEbola ? (
            <>
              <SectionTitle label="Travel Information" />
              <Row label="Country of Departure" value={fmt(screening.country_of_origin)} />
              <Row label="Point of Entry"        value={screening.entry_point_title || fmt(screening.entry_city && formatPortName(screening.entry_city))} />
              <Row label="Mode of Transport"     value={fmt(ebolaData.mode_of_transport || screening.entry_type)} />
              <Row label="Vessel / Flight No."   value={fmt(screening.transport_number)} />
              <Row label="Arrival Date"          value={fmtDate(screening.arrival_date || screening.created_at)} />

              <SectionTitle label="Travel History" />
              <Row label="Visited Affected Countries"  value={screening.visited_epidemic_area ? 'Yes' : 'No'} />
              <Row label="Contact with Confirmed Case" value={screening.contact_with_sick ? 'Yes' : 'No'} />
              <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
                <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Other Exposure</Typography>
                <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {exposures.length > 0
                    ? exposures.map(e => <Chip key={e} label={e} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.72rem' }} />)
                    : <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>None</Typography>
                  }
                </Box>
              </Box>

              <SectionTitle label="Health Assessment" />
              <Row label="Temperature (°C)" value={fmt(screening.temperature)} />
              <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
                <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Symptoms</Typography>
                <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {symptoms.length > 0
                    ? symptoms.map(s => <Chip key={s} label={s} size="small" color="error" variant="outlined" sx={{ fontSize: '0.72rem' }} />)
                    : <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>None</Typography>
                  }
                </Box>
              </Box>
            </>
          ) : (
            <>
              <SectionTitle label="Entry Information" />
              <Row label="Entry Type"              value={fmt(screening.entry_type)} />
              <Row label="County / Point of entry" value={screening.entry_point_title || fmt(screening.entry_city && formatPortName(screening.entry_city))} />
              <Row label="Country of Departure"    value={fmt(screening.country_of_origin)} />
              <Row label="Arrival Date"            value={fmtDate(screening.arrival_date || screening.created_at)} />
              <Row label="Arrival Time"            value={fmt(screening.arrival_time)} />
              <Row label="Transport Number"        value={fmt(screening.transport_number)} />
              <Row label="Countries Visited (21d)" value={(screening.countries_visited_21d || []).join(', ') || '—'} />

              <SectionTitle label="Personal Details" />
              <Row label="Full Name"     value={fmt(screening.full_name)} />
              <Row label="Sex"           value={fmt(screening.sex)} />
              <Row label="Date of Birth" value={fmtDate(screening.date_of_birth)} />
              <Row label="Age"           value={fmt(screening.age)} />
              <Row label="Nationality"   value={fmt(screening.nationality)} />
              <Row label="ID / Passport" value={fmt(screening.id_number)} />

              <SectionTitle label="Health Assessment" />
              <Row label="Temperature (°C)" value={fmt(screening.temperature)} />
              <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
                <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Symptoms</Typography>
                <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {symptoms.length > 0
                    ? symptoms.map(s => <Chip key={s} label={s} size="small" color="error" variant="outlined" sx={{ fontSize: '0.72rem' }} />)
                    : <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>None</Typography>
                  }
                </Box>
              </Box>
              <Row label="Contact with Sick Person" value={screening.contact_with_sick ? 'Yes' : 'No'} />
              <Row label="Visited Epidemic Area"    value={screening.visited_epidemic_area ? 'Yes' : 'No'} />
              <Row label="Epidemic Details"         value={fmt(screening.epidemic_details)} />

              <SectionTitle label="Vaccination" />
              <Row label="Yellow Fever Vaccinated" value={screening.yellow_fever_vaccinated ? 'Yes' : 'No'} />
              <Row label="Yellow Fever Date"       value={fmtDate(screening.yellow_fever_date)} />
            </>
          )}

          <SectionTitle label="Risk Assessment" />
          <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
            <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Risk Level</Typography>
            <Chip
              label={screening.risk_level || '—'}
              size="small"
              color={screening.risk_level === 'high_risk' ? 'error' : 'success'}
              sx={{ fontWeight: 700, textTransform: 'uppercase' }}
            />
          </Box>
          <Row label="Screened At" value={fmtDate(screening.created_at)} />
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
        This pass is read-only. Issued by UHAI · Not transferable.
      </Typography>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3500}
        onClose={() => setSnackbar(null)}
        message={snackbar || ''}
      />
    </Box>
  );
};

export default PublicPassPage;
